/**
 * Alpine.js initialization and configuration
 */
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';

// Import the language components
import { availableLanguages, popularLanguages, languageEditor } from './alpine-language-editor';
import { languageModal } from './alpine-language-modal';

// Import profile editing components
import { textEditor } from './alpine-text-editor';
import { inputEditor } from './alpine-input-editor';
import { nameUsernameEditor } from './alpine-name-username-editor';
import { selectEditor } from './alpine-select-editor';
import { multiselectEditor } from './alpine-multiselect-editor';
import { statusEditor } from './alpine-status-editor';
import { statusPostComposer } from './alpine-status-post-composer';
import { homeHeader } from './alpine-home-header';
import { cityEditor } from './alpine-city-editor';
// Import profile language modal
import { profileLanguageModal } from './profile-language-modal.js';

// Import shared profile photo uploader
import { profilePhotoUploader } from './profile-photo-uploader.js';

// Import notifications panel component
import { notificationsPanel } from '../interpals/notifications.js';

// Import quick search filters component
import { quickSearchFilters } from './alpine-quick-search.js';

// Import user carousel component
import { userCarousel } from './alpine-user-carousel.js';

// Profile hero photo carousel component
import { profileHeroCarousel } from './alpine-profile-hero-carousel.js';

// Import speakers language switcher component
import { speakersLanguageSwitcher } from './alpine-speakers-switcher.js';

// Import loading button component
import { loadingButton } from './alpine-loading-button.js';

// Import compact profile scroll hint component
import { compactProfileScrollHint } from './alpine-compact-profile-scroll-hint.js';

// Import bookmark button component
import { bookmarkButton } from './alpine-bookmark-button.js';

// Import passkey settings component
import { passkeySettings } from './alpine-passkey-settings.js';

// Import the forced identity check sheet. Eager, not route-scoped: the sheet is
// the whole page and carries the only log-out link on it, so a chunk that fails
// to load would leave the account on a blank page with no way out.
import { identityCheckSheet } from './alpine-identity-check.js';

// Import premium subscription card component
import { premiumSubscription } from './alpine-premium-subscription.js';

// Import suspension-appeal form component
import { appealForm } from './alpine-appeal-form.js';

// Import contact-form component
import { feedbackForm } from './alpine-feedback-form.js';

// Import shared image-file attacher component
import { fileAttacher } from './alpine-file-attacher.js';

// Import trip editor component
import { tripEditor } from './alpine-trip-editor.js';

// Import interests modal components
import { interestsModal, interestsDisplay, searchInterestsFilter, interestsBrowseSheet } from '../interpals/interests-modal.js';

// Import responsive filter chip component
import { responsiveFilterChip } from './alpine-responsive-filter.js';

// Import search filters modal component
import { searchFiltersModal } from './alpine-search-filters-modal.js';

// Import nearby chip component
import { nearbyChip } from './alpine-nearby-chip.js';

// Import country picker components
import { countryChips, countryPickerModal } from './alpine-country-picker.js';

// Import language picker components
import { languageChips, languagePickerModal } from './alpine-language-picker.js';

// Import city filter component for search location
import { cityFilter } from './alpine-city-filter.js';

// Import like button and likers modal components
import { likeButton } from './alpine-like-button.js';
import { likersModal } from './alpine-likers-modal.js';
import { inlineComments } from './alpine-inline-comments.js';

// Import report modal component
import { reportModal } from './report-modal.js';

// Import block & delete modal component
import { blockDeleteModal } from './block-delete-modal.js';

// Import 1:1 call components (incoming ring modal, in-call overlay, thread buttons)
import { incomingCallModal, inCallOverlay, threadCallButtons } from '../interpals/calls/call-ui.js';

// Import photo viewer components
import { mobilePhotoViewer } from './alpine-mobile-photo-viewer.js';
import { desktopPhotoViewer } from './alpine-desktop-photo-viewer.js';

// Import footer language picker component
import { footerLanguagePicker } from './alpine-footer-language-picker.js';

// Import ultra-legacy yellow member-nav collapse component
import { yellowBarCollapse } from './alpine-yellow-bar.js';

// Import web push settings toggle component

// Import the profile store
import './alpine-profile-store';

// Defines window.shareProfile for the profile header's share action
import './share-profile.js';

// Neutralizes the roots of components whose route-scoped chunk failed to load
import { ignoreComponentRoots } from './ignore-component-roots.js';

// Awaits the route-scoped chunks without letting a failure block Alpine's start
import { settleLazyComponents } from './settle-lazy-components.js';
import { completeAlpineBootstrap } from './complete-alpine-bootstrap.js';
import { isChunkLoadError, loadChunkWithRetry } from './load-chunk-with-retry.js';
import {
    CHUNK_RECOVERY_EXHAUSTED,
    CHUNK_RECOVERY_SCHEDULED,
    finishChunkLoadRecovery,
    isChunkRecoveryNavigationScheduled,
    recoverRetriedChunkLoad,
    waitForChunkRecoveryNavigation,
} from './chunk-load-recovery.js';

// Load the PM correction modal component here (not only via its own webpack
// entry) so window.correctionModal is defined before the fragile legacy vendor
// modules (jquery.notify.js, autocomplete) run later in the entry list.
// Those modules can throw at eval time and abort the rest of the bundle; loading
// correction.js as a dependency of alpine-init evaluates it up-front, so the
// pm/index.twig `x-data="correctionModal()"` scope resolves even after such an
// abort (Sentry LEGACY-R80P: "sending is not defined").
import '../interpals/pm/correction.js';

// Clear saved signup wizard state when not on the signup page to avoid stale data
try {
    const path = window.location.pathname || '';
    if (!path.includes('/signup')) {
        localStorage.removeItem('signupWizardState');
    }
} catch (e) {
    // Ignore storage access issues
}

// Configure Alpine.js
Alpine.prefix('x-');

// Register plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);

// Set up Alpine store for i18n state (translations loaded asynchronously)
// Components can use $store.i18n.ready to trigger re-renders when translations load
// Check if translations were already loaded before Alpine initialized (cached translations)
Alpine.store('i18n', {
    ready: window.I18n?.keys().length > 0
});

// Set up Alpine store for shared language data
Alpine.store('languageData', {
    available: availableLanguages,
    popular: popularLanguages,
    
    // Centralized filtering method
    getFiltered(existingCodes, searchTerm = '') {
        const term = searchTerm.toLowerCase();
        return Object.entries(this.available)
            .filter(([code, name]) => 
                !existingCodes.has(code) && 
                (name.toLowerCase().includes(term) || code.toLowerCase().includes(term))
            )
            .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB));
    }
});

// Make Alpine globally available before starting
window.Alpine = Alpine;

// Register Alpine data components
Alpine.data('languageEditor', languageEditor);
Alpine.data('languageModal', languageModal);
Alpine.data('textEditor', textEditor);
Alpine.data('inputEditor', inputEditor);
Alpine.data('nameUsernameEditor', nameUsernameEditor);
Alpine.data('selectEditor', selectEditor);
Alpine.data('multiselectEditor', multiselectEditor);
Alpine.data('statusEditor', statusEditor);
Alpine.data('statusPostComposer', statusPostComposer);
Alpine.data('homeHeader', homeHeader);
Alpine.data('cityEditor', cityEditor);
// Register profile language modal
Alpine.data('profileLanguageModal', profileLanguageModal);

// Register shared photo uploader component (used by profile page)
Alpine.data('profilePhotoUploader', profilePhotoUploader);

// Register notifications panel component
Alpine.data('notificationsPanel', notificationsPanel);


// Register quick search filters component
Alpine.data('quickSearchFilters', quickSearchFilters);

// Register user carousel component
Alpine.data('userCarousel', userCarousel);
Alpine.data('profileHeroCarousel', profileHeroCarousel);

// Register speakers language switcher component
Alpine.data('speakersLanguageSwitcher', speakersLanguageSwitcher);

// Register loading button component
Alpine.data('loadingButton', loadingButton);

// Register compact profile scroll hint component
Alpine.data('compactProfileScrollHint', compactProfileScrollHint);

// Register bookmark button component
Alpine.data('bookmarkButton', bookmarkButton);

// Register passkey settings component
Alpine.data('passkeySettings', passkeySettings);

// Register the forced identity check sheet
Alpine.data('identityCheckSheet', identityCheckSheet);

// Register premium subscription card component
Alpine.data('premiumSubscription', premiumSubscription);

// The moderator components register from route-scoped chunks further down.

// Register suspension-appeal form component
Alpine.data('appealForm', appealForm);

// Register contact-form component
Alpine.data('feedbackForm', feedbackForm);

// Register shared image-file attacher component
Alpine.data('fileAttacher', fileAttacher);

// Register trip editor component
Alpine.data('tripEditor', tripEditor);

// Register interests modal components
Alpine.data('interestsModal', interestsModal);
Alpine.data('interestsDisplay', interestsDisplay);
Alpine.data('searchInterestsFilter', searchInterestsFilter);
Alpine.data('interestsBrowseSheet', interestsBrowseSheet);

// Register responsive filter chip component
Alpine.data('responsiveFilterChip', responsiveFilterChip);

// Register search filters modal component
Alpine.data('searchFiltersModal', searchFiltersModal);

// Register nearby chip component
Alpine.data('nearbyChip', nearbyChip);

// Register country picker components
Alpine.data('countryChips', countryChips);
Alpine.data('countryPickerModal', countryPickerModal);

// Register language picker components
Alpine.data('languageChips', languageChips);
Alpine.data('languagePickerModal', languagePickerModal);

// Register city filter component
Alpine.data('cityFilter', cityFilter);

// Register like button and likers modal components
Alpine.data('likeButton', likeButton);
Alpine.data('likersModal', likersModal);
Alpine.data('inlineComments', inlineComments);

// Register report modal component
Alpine.data('reportModal', reportModal);

// Register block & delete modal component
Alpine.data('blockDeleteModal', blockDeleteModal);

// Register 1:1 call components
Alpine.data('incomingCallModal', incomingCallModal);
Alpine.data('inCallOverlay', inCallOverlay);
Alpine.data('threadCallButtons', threadCallButtons);

// Register footer language picker component
Alpine.data('footerLanguagePicker', footerLanguagePicker);

// Register ultra-legacy yellow member-nav collapse component
Alpine.data('yellowBarCollapse', yellowBarCollapse);

// Register web push settings toggle component

// Register photo viewer components
Alpine.data('mobilePhotoViewer', mobilePhotoViewer);
Alpine.data('desktopPhotoViewer', desktopPhotoViewer);

// Lazy-load page-specific components based on current route
const path = window.location.pathname;
const lazyComponents = [];
let chunkRecoveryExhausted = false;

/**
 * Reports a retry outcome without letting the report itself decide the load's
 * fate. This runs on the fulfilment path as well as the rejection one, where a
 * throw would reject a promise that had actually resolved, so a chunk whose
 * components loaded fine would be handed to ignoreComponentRoots and switched
 * off. Losing a report costs one Sentry event, which is the cheaper failure.
 */
function reportRetryOutcome(message, level) {
    try {
        window.Sentry?.captureMessage?.(message, level);
    } catch (reportingError) {
        console.warn('[alpine-init] could not report chunk retry outcome:', reportingError);
    }
}

/**
 * Queues a route-scoped component chunk. `names` lists the x-data components the
 * chunk registers, so a load failure can be confined to their roots via
 * ignoreComponentRoots. A download that fails is retried once before the
 * failure is allowed to stand — see loadChunkWithRetry for why.
 *
 * A retry that succeeds is reported at info. One that still fails refreshes the
 * page once to obtain a current runtime; only a post-refresh failure reaches
 * warning and captureException, so recovered stale runtimes do not keep their
 * Sentry error groups alive.
 */
function lazyLoad(names, load) {
    let retried = false;

    const promise = loadChunkWithRetry(load, (reason) => {
        retried = true;
        console.warn('[alpine-init] retrying component chunk after load failure:', reason);
    }).then(
        (value) => {
            if (retried) {
                reportRetryOutcome('component chunk recovered on retry', 'info');
            }

            return value;
        },
        (error) => {
            if (retried && isChunkLoadError(error)) {
                const recovery = recoverRetriedChunkLoad();
                if (recovery === CHUNK_RECOVERY_SCHEDULED) {
                    console.warn('[alpine-init] refreshing after component chunk retry failed:', error);
                    return waitForChunkRecoveryNavigation(error);
                }

                chunkRecoveryExhausted = recovery === CHUNK_RECOVERY_EXHAUSTED;
                reportRetryOutcome(
                    chunkRecoveryExhausted
                        ? 'component chunk failed after retry and refresh'
                        : 'component chunk failed after retry; refresh unavailable',
                    'warning',
                );
            }
            throw error;
        }
    );

    lazyComponents.push({ names, promise });
}

if (path.includes('/signup')) {
    lazyLoad(['signupWizard'], () =>
        import(/* webpackPrefetch: true */ './signup/index.js').then(m => Alpine.data('signupWizard', m.signupWizard))
    );
}

if (path.includes('/getting-started')) {
    lazyLoad(['gettingStartedGoals', 'gettingStartedLanguages', 'gettingStartedAbout', 'gettingStartedPhoto'], () =>
        import(/* webpackPrefetch: true */ './getting-started/index.js').then(m => {
            Alpine.data('gettingStartedGoals', m.gettingStartedGoals);
            Alpine.data('gettingStartedLanguages', m.gettingStartedLanguages);
            Alpine.data('gettingStartedAbout', m.gettingStartedAbout);
            Alpine.data('gettingStartedPhoto', m.gettingStartedPhoto);
        })
    );
}

if (path.includes('/global-feed') || path.includes('/account')) {
    lazyLoad(['globalFeedComponent'], () =>
        import(/* webpackPrefetch: true */ '../interpals/global-feed.js').then(m => Alpine.data('globalFeedComponent', m.globalFeedComponent))
    );
}

// Moderator tooling is route-scoped, and `/mod` alone is the wrong test for it.
// Two of the surfaces a moderator uses most sit outside that prefix: the photo
// page carries the ban / reuse / web-detection lightbox actions and the photo
// moderation modal, and a search result card dispatches open-moderate-modal.
// A chunk that is absent on a page that needs it fails silently — the control
// renders and clicking it does nothing — so each predicate below is drawn from
// where the component is actually reachable from, not from where it sounds like
// it belongs.
const onModeratorPage = path.includes('/mod');
const onPhotoPage = path.includes('/photo');
const onSearchPage = path.includes('/search');

if (onModeratorPage) {
    lazyLoad(['moderatorPrivileges'], () =>
        import(/* webpackPrefetch: true */ './moderation/moderator-privileges.js').then(m => Alpine.data('moderatorPrivileges', m.moderatorPrivileges))
    );
    lazyLoad(['modActivityHeatmap'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-activity-heatmap.js').then(m => Alpine.data('modActivityHeatmap', m.modActivityHeatmap))
    );
    lazyLoad(['modUserPreview'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-user-preview.js').then(m => Alpine.data('modUserPreview', m.modUserPreview))
    );
}

/**
 * The idempotency claim and the signed-POST helper, which every moderation
 * submit path reads off window at submit time: moderate.js, photo-mod-modal.js
 * and the reports page's inline handler. None of them can import the modules —
 * moderate.js is exercised by running its source as a plain script in a vm
 * sandbox, and the reports handler is inline in Twig — so the loader owns the
 * ordering, and each group below resolves this before the module that needs it.
 *
 * Getting that order wrong is not visible: the claim falls back to a random
 * per-submission id, and the cost is a duplicated moderation action rather than
 * an error.
 */
const moderationActionGlobals = () => Promise.all([
    import(/* webpackPrefetch: true */ './moderation-action-idempotency.js'),
    import(/* webpackPrefetch: true */ './moderation-action-submit.js'),
]);

// The moderate modal and everything that submits a moderation action. Reachable
// from every /mod page and from a search result card's Moderate menu item.
if (onModeratorPage || onSearchPage) {
    // Rides with the moderate form, which the modal injects on both surfaces.
    lazyLoad(['modIdentityCheckChips'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-identity-check-chips.js')
            .then(m => Alpine.data('modIdentityCheckChips', m.modIdentityCheckChips))
    );
    // moderate.js injects its own `moderateUserModal` and `modMessagesModal`
    // roots into the page rather than mounting roots a template rendered, so a
    // chunk that fails to load leaves no root behind to neutralize.
    lazyLoad([], () =>
        moderationActionGlobals().then(() => import(/* webpackPrefetch: true */ '../interpals/moderate.js'))
    );
}

// Photo moderation. The lightbox actions live on the photo page, and the
// reported-user card on /mod/reports opens the web-detection modal, so this set
// spans both surfaces rather than one.
if (onModeratorPage || onPhotoPage) {
    lazyLoad(['photoModModal'], () =>
        moderationActionGlobals()
            .then(() => import(/* webpackPrefetch: true */ '../interpals/photo-mod-modal.js'))
            .then(m => Alpine.data('photoModModal', m.photoModModal))
    );
    // These three register window.openPhoto*Modal rather than an x-data
    // component, so there is no root to neutralize if the chunk fails.
    lazyLoad([], () =>
        Promise.all([
            import(/* webpackPrefetch: true */ '../interpals/photo-ban-lightbox.js'),
            import(/* webpackPrefetch: true */ '../interpals/photo-reuse-lightbox.js'),
            import(/* webpackPrefetch: true */ '../interpals/photo-web-detection-lightbox.js'),
        ])
    );
}

if (path.includes('/mod/appeals')) {
    lazyLoad(['modAppealsPage', 'modAppealDecide'], () =>
        import(/* webpackPrefetch: true */ '../interpals/mod-appeals.js').then(m => {
            Alpine.data('modAppealsPage', m.modAppealsPage);
            Alpine.data('modAppealDecide', m.modAppealDecide);
        })
    );
}

if (path.includes('/mod/support')) {
    lazyLoad(['modSupportThread', 'modSupportComposer'], () =>
        import(/* webpackPrefetch: true */ '../interpals/mod-support.js').then(m => {
            Alpine.data('modSupportThread', m.modSupportThread);
            Alpine.data('modSupportComposer', m.modSupportComposer);
        })
    );
    lazyLoad(['modSupportToolbar', 'modTranslateMessage'], () =>
        import(/* webpackPrefetch: true */ '../interpals/mod-support-toolbar.js').then(m => {
            Alpine.data('modSupportToolbar', m.modSupportToolbar);
            Alpine.data('modTranslateMessage', m.modTranslateMessage);
        })
    );
}

if (path.includes('/mod/cases')) {
    lazyLoad(['modCasesPage'], () =>
        import(/* webpackPrefetch: true */ '../interpals/mod-cases.js').then(m => Alpine.data('modCasesPage', m.modCasesPage))
    );
}

if (path.includes('/mod/deleted-accounts')) {
    lazyLoad(['modDeletedAccountsPageModal', 'modDeletedAccountRow'], () =>
        import(/* webpackPrefetch: true */ '../interpals/mod-deleted-accounts.js').then(m => {
            Alpine.data('modDeletedAccountsPageModal', m.modDeletedAccountsPageModal);
            Alpine.data('modDeletedAccountRow', m.modDeletedAccountRow);
        })
    );
}

if (path.includes('/mod/moderators')) {
    lazyLoad(['capabilityPicker'], () =>
        import(/* webpackPrefetch: true */ './alpine-capability-picker.js').then(m => Alpine.data('capabilityPicker', m.capabilityPicker))
    );
}

if (path.includes('/mod/related-accounts')) {
    lazyLoad(['modRelatedPhotoSweep'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-related-photo-sweep.js')
            .then(m => Alpine.data('modRelatedPhotoSweep', m.modRelatedPhotoSweep))
    );
}

if (path.includes('/mod/photo-tags')) {
    lazyLoad(['photoTagFilters'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-photo-tag-filters.js')
            .then(m => Alpine.data('photoTagFilters', m.photoTagFilters))
    );
}

if (path.includes('/mod/email-rejections')) {
    lazyLoad(['modEmailRejections'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-email-rejections.js')
            .then(m => Alpine.data('modEmailRejections', m.modEmailRejections))
    );
}

if (path.includes('/mod/settings/deletion-rule/')) {
    lazyLoad(['modTextFilterDeletions'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-text-filter-deletions.js')
            .then(m => Alpine.data('modTextFilterDeletions', m.modTextFilterDeletions))
    );
} else if (path.includes('/mod/settings')) {
    lazyLoad(['modSettings'], () =>
        import(/* webpackPrefetch: true */ './moderation/mod-settings.js').then(m => Alpine.data('modSettings', m.modSettings))
    );
}

// Wait for lazy components and the async i18n payload, then start Alpine so
// components never render raw `common.foo` keys before translations load.
// I18n.ready() returns the loadFromUrl() promise; on a localStorage cache hit
// (returning visitor on the same translations version) the cache branch runs
// synchronously and populates I18n.translations before the promise resolves,
// so the only added latency is a single microtask — no observable delay.
// Fetch failures resolve the promise without populating translations, so
// Alpine still starts; the failure mode (raw keys) matches existing behavior.
//
// settleLazyComponents keeps a failed chunk from blocking the start; handling
// its rejection also keeps it away from Sentry's unhandled-rejection capture, so
// report it explicitly here. What reaches this point has already survived one
// retry, so it is either an asset that is genuinely unreachable or a connection
// that failed twice — both have to stay visible.
const translationsReady = window.I18n?.ready ? window.I18n.ready() : Promise.resolve();
const componentsReady = settleLazyComponents(lazyComponents, (reason, names) => {
    console.error('[alpine-init] deferred component unavailable:', reason);
    window.Sentry?.captureException?.(reason);
    ignoreComponentRoots(names);
});

completeAlpineBootstrap(translationsReady, componentsReady, {
    finishRecovery: finishChunkLoadRecovery,
    isNavigationScheduled: isChunkRecoveryNavigationScheduled,
    isRecoveryExhausted: () => chunkRecoveryExhausted,
    start: () => Alpine.start(),
});

// Export for use in other modules
export default Alpine;
